import { Typography } from '@material-ui/core';
import { Box, Button } from '@superdispatch/ui-lab';
import { Fragment, ReactElement, useMemo, useRef } from 'react';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import {
  createSearchCriteria,
  getActiveSearchCount,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { useScrollToTop } from 'shared/routing/ScrollToTop';
import styled from 'styled-components';
import { Heading } from '../../core/Heading';
import { getDriverSuggestedLoadOpenSource } from '../../core/DriverSuggestedLoadAnalytics';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { PostingListPagination } from '../../core/postings/PostingListPagination';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import { PostingsPageState } from '../../core/postings/PostingsPageState';
import { SimilarLoads } from '../../core/similar-loads/SimilarLoads';
import { isEmptyCriteria } from '../../data/dto/PostingsPageParamsDTO';
import { PostingListCard } from './PostingListCard';
import { PostingListEmpty } from './PostingListEmpty';
import { useLoadRequestSuggestionsNudgingLoadGuid } from 'shared/modules/load-suggestions/LoadSuggestionsNudgingHelper';
import { PostingsListSuggestionsOnRequestBanner } from '../../core/nudging/PostingsListSuggestionsOnRequestBanner';

const Wrapper = styled.div`
  flex: 1 1;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;
`;

const Caption = styled.div`
  display: flex;
  flex-shrink: 0;
  margin: 2px 8px;
  align-items: center;
`;

const Styled1 = styled.div`
  flex: 1;
`;
const Styled2 = styled.div`
  flex: 1;
`;
const Styled3 = styled.div`
  margin: 16px 8px 32px;
`;

export function PostingList({
  postings,
  postingsPageError,
  postingsPagination,
  isPostingsPageLoading,
}: PostingsPageState): ReactElement {
  const ref = useRef(null);
  const { setParams, params } = usePostingsPageContext();

  const isLastPage =
    !!postingsPagination &&
    params.current_page + 1 >=
      Math.ceil(postingsPagination.count / params.current_page_size);

  const activeSearchCount = useMemo(
    () => getActiveSearchCount(params.search_criteria),
    [params],
  );

  const suggestedLoadOpenSource = useMemo(
    () => getDriverSuggestedLoadOpenSource(params),
    [params],
  );

  useScrollToTop(
    ref.current,
    [params.current_tab, JSON.stringify(params.search_criteria)],
    { skipInitialMount: true },
  );

  const loadSuggestionsNudgingBannerLoadGuid =
    useLoadRequestSuggestionsNudgingLoadGuid();

  return (
    <Wrapper ref={ref}>
      {postingsPageError ? (
        <Heading>Oops! Something Went wrong.</Heading>
      ) : !postings || isPostingsPageLoading ? (
        <Heading>Loading…</Heading>
      ) : (
        <>
          {postingsPagination && !isEmptyCriteria(params) && (
            <Caption>
              <Styled1>
                {postingsPagination.count > 0 && (
                  <Typography>
                    {postingsPagination.count}{' '}
                    {formatPlural(
                      postingsPagination.count,
                      'Load Found',
                      'Loads Found',
                    )}
                  </Typography>
                )}
              </Styled1>

              {activeSearchCount > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setParams((prev) => ({
                      ...prev,
                      current_saved_search_guid: undefined,
                      search_criteria: createSearchCriteria({}),
                    }));
                    trackLoadboardEvent({
                      name: 'SLB: UI Interaction',
                      type: 'Filter: Clicked Clear',
                    });
                  }}
                >
                  Clear · {activeSearchCount}
                </Button>
              )}
            </Caption>
          )}

          {postings.length === 0 ? (
            <PostingListEmpty />
          ) : (
            <>
              <Styled2>
                {postings.map((item) => (
                  <Fragment key={item.load.guid}>
                    <Box margin="xsmall">
                      <PostingListCard
                        posting={item}
                        suggestedLoadOpenSource={suggestedLoadOpenSource}
                      />
                    </Box>
                    {loadSuggestionsNudgingBannerLoadGuid ===
                      item.load.guid && (
                      <Box margin="xsmall">
                        <PostingsListSuggestionsOnRequestBanner />
                      </Box>
                    )}
                  </Fragment>
                ))}
              </Styled2>

              {isLastPage && (
                <Styled3>
                  <SimilarLoads
                    existingPostings={postings}
                    renderLoadCard={(posting) => (
                      <PostingListCard
                        posting={posting}
                        source="Similar Loads"
                      />
                    )}
                  />
                </Styled3>
              )}
            </>
          )}
        </>
      )}

      <PostingListPagination pagination={postingsPagination} />
    </Wrapper>
  );
}
